import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './contexts/AuthContext';
import { useError } from './contexts/ErrorContext';
import { useFirebase } from './contexts/FirebaseContext';
import { Box, Typography, Paper, TextField, Stack, CircularProgress } from '@mui/material';
import { doc, updateDoc } from 'firebase/firestore';
import { useNavigate, useLocation } from 'react-router-dom';
import { useGameUtils } from './utilities/GameUtils';
import newGameIcon from './assets/images/icon-plus-circle--white.svg';
import findAGameIcon from './assets/images/icon-search--green.svg';
import bgFindGame from './assets/images/bg-find-game-button.png';
import bgStartGame from './assets/images/bg-start-game-button.png';
import palette from './assets/sass/palette.scss';
import GameListV2 from './components/GameListV2';
const Dashboard = () => {
  const { currentUser } = useAuth();
  const { logFirebaseError } = useError();
  const { db } = useFirebase();
  const navigate = useNavigate();
  const location = useLocation();
  const prevLocationRef = useRef(location.pathname);
  const [pendingGames, setPendingGames] = useState([]);
  const [currentGames, setCurrentGames] = useState([]);
  const [previousGames, setPreviousGames] = useState([]);
  const [previousGamesIPlayedIn, setPreviousGamesIPlayedIn] = useState([]);
  const [myGames, setMyGames] = useState([]);
  const [isLoadingGames, setIsLoadingGames] = useState(true);
  const [gamesYouAreInvitedTo, setGamesYouAreInvitedTo] = useState([]);
  const [confirmedGamesWhereIAmAPlayer, setConfirmedGamesWhereIAmAPlayer] = useState([]);

  const ENABLE_CHATBOT = false;

  const { fetchAllGamesForUser } = useGameUtils();

  const _fetchAllGamesForUser = useCallback(
    async (uid) => {
      try {
        setIsLoadingGames(true);
        const {
          scheduledGames,
          previousGames,
          pendingGames,
          myGames,
          gamesYouAreInvitedTo,
          previousGamesIPlayedIn,
          confirmedGamesWhereIAmAPlayer,
        } = await fetchAllGamesForUser(uid);
        setCurrentGames(scheduledGames);
        setPreviousGames(previousGames);
        setGamesYouAreInvitedTo(gamesYouAreInvitedTo);
        setPendingGames(pendingGames);
        setMyGames(myGames);
        setPreviousGamesIPlayedIn(previousGamesIPlayedIn);
        setConfirmedGamesWhereIAmAPlayer(confirmedGamesWhereIAmAPlayer);
      } catch (error) {
        console.error('Error fetching games:', error);
      } finally {
        setIsLoadingGames(false);
      }
    },
    [fetchAllGamesForUser]
  );

  const cancelGame = useCallback(
    async (game) => {
      try {
        const gameRef = doc(db, 'games', game.id);
        await updateDoc(gameRef, {
          active: false,
        });
      } catch (error) {
        console.error('Error deleting game:', error);
        await logFirebaseError(error, 'cancel_game', { gameId: game.id });
      } finally {
        _fetchAllGamesForUser(currentUser.uid);
      }
    },
    [db, logFirebaseError, currentUser, _fetchAllGamesForUser]
  );

  useEffect(() => {
    _fetchAllGamesForUser(currentUser.uid);
  }, [currentUser, _fetchAllGamesForUser]);

  // Refresh games when returning to dashboard
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && currentUser?.uid) {
        _fetchAllGamesForUser(currentUser.uid);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentUser, _fetchAllGamesForUser]);

  // Refresh games when navigating to dashboard from another page
  useEffect(() => {
    // Check if we just navigated TO the dashboard from somewhere else
    if (prevLocationRef.current !== location.pathname && location.pathname === '/dashboard') {
      if (currentUser?.uid) {
        _fetchAllGamesForUser(currentUser.uid);
      }
    }
    prevLocationRef.current = location.pathname;
  }, [location.pathname, currentUser, _fetchAllGamesForUser]);

  function handleNewGame() {
    navigate('/new-game');
  }

  function handleFindAGame() {
    navigate('/find-a-game');
  }

  const BigGameButton = ({ label, description, icon, iconSize = 64, onClick, bgAttributes }) => {
    return (
      <Stack
        onClick={onClick}
        sx={{
          borderRadius: '32px',
          padding: '16px',
          justifyContent: 'space-between',
          minHeight: '250px',
          cursor: 'pointer',
          position: 'relative',
          color: 'white',
          backgroundColor: bgAttributes.color,
          overflow: 'hidden',
          '&::before': {
            content: '""',
            display: 'block',
            position: 'absolute',
            top: bgAttributes.top,
            right: bgAttributes.right,
            width: '66%',
            height: '200%',
            backgroundImage: `url(${bgAttributes.image})`,
            backgroundSize: bgAttributes.size,
            backgroundRepeat: 'no-repeat',
            transform: `rotate(${bgAttributes.angle})`,
            zIndex: 0,
          },
        }}
      >
        <img src={icon} alt="" width={iconSize} style={{ zIndex: 1 }} />
        <Box sx={{ width: '60%', zIndex: 1 }}>
          <Typography variant="factorA" component="p" sx={{ fontWeight: '500', fontSize: '24px' }}>
            {label}
          </Typography>
          <Typography component="small" variant="small" color="white">
            {description}
          </Typography>
        </Box>
      </Stack>
    );
  };

  // Loading component for game sections
  const GameSectionLoading = () => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        py: 4,
        minHeight: '120px',
      }}
    >
      <CircularProgress size={40} />
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', maxWidth: '1060px', margin: '0 auto' }}>
      <Paper sx={{ flexGrow: 1, paddingX: { xs: 2, sm: 6 } }} variant="rightPane">
        <Box sx={{ mb: 3 }}>
          {ENABLE_CHATBOT && (
            <>
              <Typography sx={{ my: 2 }}>
                Need help setting up your next round? Tell me what you need!
              </Typography>

              <TextField
                multiline
                fullWidth
                variant="outlined"
                id="ai-search"
                placeholder="Example: Set me up a game for friday with Jack at St Andrews"
              />
            </>
          )}
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ my: 3 }}>
          <Box sx={{ flex: 3 }}>
            <BigGameButton
              label="Start a Game"
              description="Create your own game, invite your friends or open it up to the public."
              icon={newGameIcon}
              iconSize="64"
              onClick={handleNewGame}
              bgAttributes={{
                image: bgStartGame,
                top: '-80px',
                right: '-200px',
                color: palette.mediumPurple,
                size: '250px',
                angle: '-10deg',
              }}
              fullWidth
            />
          </Box>
          <Box sx={{ flex: 2 }}>
            <BigGameButton
              label="Find a game"
              description="Find a game with WAGL users in your area."
              icon={findAGameIcon}
              iconSize="54"
              onClick={handleFindAGame}
              bgAttributes={{
                image: bgFindGame,
                top: '-50%',
                right: '-150px',
                color: palette.darkGreen,
                size: '100%',
                angle: '-60deg',
              }}
              fullWidth
            />
          </Box>
        </Stack>

        {!isLoadingGames && pendingGames?.length === 0 && currentGames?.length === 0 && previousGames?.length === 0 && myGames?.length === 0 && gamesYouAreInvitedTo?.length === 0 && confirmedGamesWhereIAmAPlayer?.length === 0 ? (
          <Box sx={{ mt: 4 }}>
            <Typography component="h3" variant="sectionHeader">
              Get started by creating your first game!
            </Typography>
          </Box>
        ) : null}

        <Box sx={{ pt: 2, pb: 3 }}>
          <Typography component="h3" variant="sectionHeader" sx={{ px: { xs: 2, sm: 0 }, mb: { xs: confirmedGamesWhereIAmAPlayer?.length > 2 ? 7 : 0, sm: 0 } }}>
            Confirmed Games
          </Typography>
          {isLoadingGames ? (
            <GameSectionLoading />
          ) : confirmedGamesWhereIAmAPlayer?.length > 0 ? (
            <GameListV2
              games={confirmedGamesWhereIAmAPlayer}
              currentUser={currentUser}
              cancelGame={cancelGame}
              useCarousel={confirmedGamesWhereIAmAPlayer.length > 2}
              isOrganiser={false}
              isPending={false}
              isPast={false}
            />
          ) : null}
        </Box>

        {myGames?.length > 0 && (
          <Box sx={{ pt: 3 }}>
            <Typography component="h3" variant="sectionHeader" sx={{ px: { xs: 2, sm: 0 }, mb: { xs: myGames?.length > 2 ? 7 : 0, sm: 0 } }}>
              Games I'm organising
            </Typography>
            {isLoadingGames ? (
              <GameSectionLoading />
            ) : myGames?.length > 0 ? (
              <GameListV2
                games={myGames}
                currentUser={currentUser}
                cancelGame={cancelGame}
                useCarousel={myGames.length > 2}
                isOrganiser={true}
                isPast={false}
              />
            ) : (
              <Box sx={{ my: 4 }}>You have no matches that you have organised</Box>
            )}
          </Box>
        )}
        <Box sx={{ pt: 3 }}>
          <Typography component="h3" variant="sectionHeader" sx={{ px: { xs: 2, sm: 0 }, mb: { xs: gamesYouAreInvitedTo?.length > 2 ? 7 : 0, sm: 0 } }}>
            Games I've been invited to
          </Typography>
          {isLoadingGames ? (
            <GameSectionLoading />
          ) : gamesYouAreInvitedTo?.length > 0 ? (
            <GameListV2
              games={gamesYouAreInvitedTo}
              currentUser={currentUser}
              cancelGame={cancelGame}
              useCarousel={gamesYouAreInvitedTo.length > 2}
              isOrganiser={false}
              isPending={true}
              isPast={false}
            />
          ) : (
            <Box sx={{ my: 4 }}>No pending matches</Box>
          )}
        </Box>

        {/* <Box sx={{ pt: 3 }}>
          <Typography component="h3" variant="sectionHeader">
            Next Scheduled Matches
          </Typography>
          {isLoadingGames ? (
            <GameSectionLoading />
          ) : currentGames?.length > 0 ? (
            <GameList
              games={currentGames}
              currentUser={currentUser}
              cancelGame={cancelGame}
              useCarousel={currentGames.length > 2}
            />
          ) : (
            <Box sx={{ my: 4 }}>No upcoming matches</Box>
          )}
        </Box> */}

        <Box sx={{ pt: 2, pb: 3 }}>
          <Typography component="h3" variant="sectionHeader" sx={{ px: { xs: 2, sm: 0 }, mb: { xs: previousGamesIPlayedIn?.length > 2 ? 7 : 0, sm: 0 } }}>
            Past Games
          </Typography>
          {isLoadingGames ? (
            <GameSectionLoading />
          ) : previousGamesIPlayedIn?.length > 0 ? (
            <GameListV2
              games={previousGamesIPlayedIn}
              currentUser={currentUser}
              cancelGame={cancelGame}
              useCarousel={previousGamesIPlayedIn.length > 2}
              isPast={true}
            />
          ) : (
            <Box sx={{ my: 4 }}>No previous matches</Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default Dashboard;
